import type { PermissionAction, ScreenId } from "@agentcall/core/type/permission_type"
import { useSetAtom } from "jotai"
import * as React from "react"
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Home,
  LogOut,
  Settings,
  User,
  UserPlus,
  Users,
} from "react-feather"
import { Link, useNavigate } from "react-router"
import logo from "@/assets/logo.png"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { logoutAtom } from "@/features/auth/stores/authAtoms"
import { STRIPE_LOGIN_URL } from "@/lib/config"
import { cn } from "@/lib/utils"

export interface MenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  href?: string
  children?: MenuItem[]
  onClick?: () => void
  /** 権限チェック用の画面識別子 */
  screenId?: ScreenId
  /** 必要な権限アクション（デフォルト: read） */
  requiredAction?: PermissionAction
}

interface SidebarProps {
  className?: string
  items: MenuItem[]
  activeId?: string
  onItemClick?: (item: MenuItem) => void
  user?: {
    name: string
    role: string
    avatar?: string
  }
  onLogout?: () => void | Promise<void>
  /** プロフィール設定へのアクセス権限 */
  canAccessProfile?: boolean
  /** 開発モードで表示するロール値 */
  devRole?: string
}

const SidebarItem: React.FC<{
  item: MenuItem
  isActive?: boolean
  isExpanded?: boolean
  level?: number
  activeId?: string
  onToggle?: () => void
  onClick?: () => void
}> = ({ item, isActive, isExpanded, level = 0, activeId, onToggle, onClick }) => {
  const hasChildren = item.children && item.children.length > 0
  const isSubItem = level > 0
  const navigate = useNavigate()

  const handleClick = (e: React.MouseEvent) => {
    if (item.href && !hasChildren) {
      e.preventDefault()
      navigate(item.href)
    }
    if (hasChildren && onToggle) {
      onToggle()
    } else if (item.onClick) {
      item.onClick()
    } else if (onClick) {
      onClick()
    }
  }

  if (isSubItem) {
    const content = (
      <div
        className={cn(
          "relative flex items-center gap-[9px] pl-[17px] pr-0 py-[5px] cursor-pointer",
          isActive &&
            "before:absolute before:inset-0 before:bg-[#ebf0f6] before:rounded-[5px] before:ml-[12px] before:mr-[26px]"
        )}
        onClick={!item.href ? handleClick : undefined}
      >
        <span
          className={cn(
            "relative font-medium text-[14px] text-[#3f4652] w-[150px]",
            isActive && "text-[#354267]"
          )}
        >
          {item.label}
        </span>
      </div>
    )

    return item.href ? (
      <Link to={item.href} onClick={handleClick} className="no-underline">
        {content}
      </Link>
    ) : (
      content
    )
  }

  const mainContent = (
    <div
      className={cn(
        "relative flex items-center justify-between px-[20px] py-[5px] cursor-pointer w-full",
        isActive &&
          !hasChildren &&
          "before:absolute before:inset-0 before:bg-[#ebf0f6] before:rounded-[5px] before:mx-[9px]"
      )}
      onClick={!item.href || hasChildren ? handleClick : undefined}
    >
      <div className="relative flex items-center gap-[10px]">
        {item.icon && (
          <span className="flex-shrink-0 w-[14px] h-[14px] text-[#3f4652]">{item.icon}</span>
        )}
        <span
          className={cn(
            "relative font-medium text-[14px] text-[#3f4652] whitespace-nowrap",
            isActive && !hasChildren && "text-[#354267]"
          )}
        >
          {item.label}
        </span>
      </div>
      {hasChildren && (
        <span className="flex-shrink-0 w-[14px] h-[14px] text-[#3f4652]">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      )}
    </div>
  )

  const wrappedContent =
    item.href && !hasChildren ? (
      <Link to={item.href} onClick={handleClick} className="no-underline">
        {mainContent}
      </Link>
    ) : (
      mainContent
    )

  return (
    <>
      {wrappedContent}
      {hasChildren && isExpanded && (
        <div className="border-l border-slate-200 ml-[26px] mt-[5px]">
          {item.children?.map((child) => (
            <SidebarItem
              key={child.id}
              item={child}
              isActive={child.id === activeId}
              level={level + 1}
              onClick={() => onClick?.()}
            />
          ))}
        </div>
      )}
    </>
  )
}

export const Sidebar: React.FC<SidebarProps> = ({
  className,
  items,
  activeId,
  onItemClick,
  user,
  onLogout,
  canAccessProfile = true,
  devRole,
}) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set())
  const [isLoggingOut, setIsLoggingOut] = React.useState(false)
  const navigate = useNavigate()
  const logout = useSetAtom(logoutAtom)

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      if (onLogout) {
        await onLogout()
      }
      logout()
      navigate("/signin")
    } catch (error) {
      console.error("ログアウトに失敗しました:", error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  const handleProfileSettings = () => {
    navigate("/profile")
  }

  const handleToggle = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleItemClick = (item: MenuItem) => {
    if (onItemClick) {
      onItemClick(item)
    }
  }

  return (
    <div
      className={cn(
        "bg-white flex flex-col h-full w-[235px] shadow-[4px_0px_6px_0px_rgba(19,46,90,0.05)]",
        className
      )}
    >
      {/* Logo Section */}
      <div className="flex flex-col items-center justify-center px-[25px] pt-[35px] pb-[20px] gap-2">
        <img src={logo} alt="AGENTCALL エージェントコール" className="w-full h-auto" />
      </div>

      {/* Menu Items */}
      <div className="flex-1 flex flex-col gap-[10px] mt-[20px]">
        {items.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            isActive={item.id === activeId || item.children?.some((child) => child.id === activeId)}
            isExpanded={expandedItems.has(item.id)}
            activeId={activeId}
            onToggle={() => handleToggle(item.id)}
            onClick={() => handleItemClick(item)}
          />
        ))}
      </div>

      {/* User Profile Section */}
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isLoggingOut}>
            <button
              type="button"
              className={cn(
                "flex items-center justify-between w-full px-[25px] py-[20px] border-t border-slate-100",
                "hover:bg-slate-50 transition-colors cursor-pointer",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-inset",
                isLoggingOut && "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-center gap-[10px]">
                <div className="flex flex-col text-left">
                  <span className="text-[14px] font-medium text-[#3f4652]">{user.name}</span>
                  <span className="text-[12px] text-[#9eafc7]">{user.role}</span>
                  {import.meta.env.DEV && devRole && (
                    <span className="text-[11px] text-orange-500 font-mono">[{devRole}]</span>
                  )}
                </div>
              </div>
              {isLoggingOut ? (
                <Spinner className="size-4 text-[#3f4652]" />
              ) : (
                <ChevronDown size={16} className="text-[#3f4652]" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-[200px]">
            {canAccessProfile && (
              <>
                <DropdownMenuItem
                  onClick={handleProfileSettings}
                  className="cursor-pointer gap-[10px]"
                >
                  <Settings size={14} className="text-[#3f4652]" />
                  <span>プロフィール設定</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={cn(
                "cursor-pointer gap-[10px] text-red-600 focus:text-red-600 focus:bg-red-50",
                isLoggingOut && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoggingOut ? <Spinner className="size-3.5" /> : <LogOut size={14} />}
              <span>{isLoggingOut ? "ログアウト中..." : "ログアウト"}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

// Default menu items based on Figma design
export const defaultMenuItems: MenuItem[] = [
  {
    id: "cases",
    label: "案件管理",
    icon: <Briefcase size={14} />,
    screenId: "tickets",
    children: [
      { id: "cases-list", label: "案件一覧", href: "/tickets", screenId: "tickets" },
      {
        id: "cases-new",
        label: "新規案件作成",
        href: "/tickets/add",
        screenId: "tickets",
        requiredAction: "create",
      },
    ],
  },
  {
    id: "properties",
    label: "物件管理",
    icon: <Home size={14} />,
    screenId: "properties",
    children: [
      { id: "properties-list", label: "物件一覧", href: "/properties", screenId: "properties" },
      {
        id: "properties-new",
        label: "新規物件作成",
        href: "/properties/add",
        screenId: "properties",
        requiredAction: "create",
      },
    ],
  },
  {
    id: "tenants",
    label: "賃借人管理",
    icon: <Users size={14} />,
    screenId: "tenants",
    children: [
      { id: "tenants-list", label: "賃借人一覧", href: "/tenants", screenId: "tenants" },
      {
        id: "tenants-new",
        label: "新規賃借人作成",
        href: "/tenants/add",
        screenId: "tenants",
        requiredAction: "create",
      },
    ],
  },
  {
    id: "new-tenant",
    label: "新規入居申込",
    icon: <UserPlus size={14} />,
    screenId: "tenant_applicants",
    children: [
      {
        id: "tenant-applicants-list",
        label: "新規入居者申込一覧",
        href: "/tenant-applicants",
        screenId: "tenant_applicants",
      },
    ],
  },
  {
    id: "owners",
    label: "オーナー管理",
    icon: <User size={14} />,
    screenId: "owners",
    children: [{ id: "owners-list", label: "オーナー一覧", href: "/owners", screenId: "owners" }],
  },
  {
    id: "payments",
    label: "お支払い管理",
    icon: <CreditCard size={14} />,
    screenId: "payments",
    onClick: () => {
      window.open(STRIPE_LOGIN_URL, "_blank")
    },
  },
  {
    id: "system",
    label: "システム管理",
    icon: <Settings size={14} />,
    children: [
      {
        id: "admin-users",
        label: "管理ユーザー一覧",
        href: "/admin-users",
        screenId: "admin_users",
      },
      {
        id: "roles",
        label: "ロール管理",
        href: "/roles",
        screenId: "roles",
      },
      {
        id: "organizations",
        label: "組織管理",
        href: "/organizations",
        screenId: "organizations",
      },
    ],
  },
]

export default Sidebar
